import { createContext, useContext, type ReactNode } from 'react';
import { Maximize, Minimize } from 'lucide-react';
import { ToolbarIconButton } from './ToolbarIconButton';

/**
 * Fullscreen mode for the Labs takeover.
 *
 * The Labs shell (`web`) owns the URL-backed state and decides whether the
 * active page is fullscreen-capable (from the route manifest flag). Game
 * pages opt in by setting `fullscreen: true` on their `definePage`, then
 * render {@link LabsFullscreenToggleButton} in their own header. The shell
 * hides the Labs aside (back chip, identity, search, nav) while fullscreen
 * is on, so the game fills the viewport; both enter and exit live in the
 * game's own header via the one toggle.
 *
 * The context + button live in `@reflex/ui` so plugin game pages can import
 * them without crossing the web boundary. The provider is mounted by
 * `LabsShell` (web); outside it (Storybook, marketing previews, non-Labs
 * routes) the consumer returns `null` and the button renders nothing.
 */

export interface LabsFullscreenContextValue {
  /** Whether the current route opts into fullscreen (the "is a game" flag). */
  available: boolean;
  /** Whether fullscreen is currently on. */
  fullscreen: boolean;
  /** Toggle fullscreen on/off. */
  toggle: () => void;
}

const LabsFullscreenContext = createContext<LabsFullscreenContextValue | null>(null);

export interface LabsFullscreenProviderProps {
  value: LabsFullscreenContextValue;
  children: ReactNode;
}

/** Mount in `LabsShell` to feed the URL-backed state to game pages. */
export function LabsFullscreenProvider({ value, children }: LabsFullscreenProviderProps) {
  return <LabsFullscreenContext.Provider value={value}>{children}</LabsFullscreenContext.Provider>;
}

/**
 * Read the Labs fullscreen context. Returns `null` outside the Labs shell
 * (or before it mounts), so callers can self-hide.
 */
export function useLabsFullscreen(): LabsFullscreenContextValue | null {
  return useContext(LabsFullscreenContext);
}

interface LabsFullscreenToggleButtonProps {
  /** Extra class on the underlying `ToolbarIconButton`. */
  className?: string;
}

/**
 * A single icon-only toggle for the Labs fullscreen mode, dropped into a
 * game page's own header. Shows "Enter fullscreen" (Maximize) when off and
 * "Exit fullscreen" (Minimize) when on, so both directions live in the same
 * header — the game stays in control of its own chrome. Renders nothing
 * when the page isn't fullscreen-capable (e.g. rendered outside the Labs
 * shell in Storybook, or on a non-game Labs page).
 */
export function LabsFullscreenToggleButton({ className }: LabsFullscreenToggleButtonProps = {}) {
  const ctx = useLabsFullscreen();
  if (!ctx?.available) return null;
  const Icon = ctx.fullscreen ? Minimize : Maximize;
  const label = ctx.fullscreen ? 'Exit fullscreen' : 'Enter fullscreen';
  return (
    <ToolbarIconButton
      icon={Icon}
      label={label}
      onClick={ctx.toggle}
      data-testid="labs-fullscreen-toggle"
      data-fullscreen={ctx.fullscreen ? 'true' : undefined}
      className={className}
    />
  );
}
